/**
 * TagPicker — Reusable tag picker component for tasks and topics.
 *
 * Features:
 * - Multi-select tag assignment with colored pills
 * - Inline tag creation (type a new name and press Enter)
 * - Small "Manage Tags" popover for rename, recolor, and delete
 * - 10 preset color options
 */

import { useState, type ReactElement } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api"
import type { Tag } from "@/types/task.types"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"
import { useToast } from "@/hooks/use-toast"
import { cn } from "@/lib/utils"
import { Check, Plus, X, Tags, Settings2, Trash2, Pencil } from "lucide-react"

// 10 preset color options
const TAG_COLORS = [
  { value: "#3b82f6", label: "Blue" },
  { value: "#ef4444", label: "Red" },
  { value: "#22c55e", label: "Green" },
  { value: "#f59e0b", label: "Amber" },
  { value: "#8b5cf6", label: "Purple" },
  { value: "#ec4899", label: "Pink" },
  { value: "#06b6d4", label: "Cyan" },
  { value: "#f97316", label: "Orange" },
  { value: "#6366f1", label: "Indigo" },
  { value: "#64748b", label: "Slate" },
]

// Task labels belong to a board (docs/boards-api.md §6). Without a board the
// API answers General's labels plus site tags, which is what topics and CTAs
// have always read.
function tagListParams(siteId?: number | null, board?: string) {
  if (board) return { board }
  return siteId ? { site_id: siteId } : undefined
}

/**
 * Small colored tag badge used in lists and pickers.
 */
export function TagBadge({ tag, onRemove, size = "sm" }: { tag: Tag; onRemove?: () => void; size?: "sm" | "xs" }) {
  const style = tag.color
    ? { backgroundColor: tag.color + "20", color: tag.color, borderColor: tag.color + "40" }
    : {}

  return (
    <Badge
      variant="outline"
      className={cn(
        "font-normal gap-1 whitespace-nowrap",
        size === "xs" ? "text-[10px] px-1.5 py-0 h-[18px]" : "text-xs px-2 py-0.5",
      )}
      style={style}
    >
      {tag.color && (
        <span
          className={cn("rounded-full flex-shrink-0", size === "xs" ? "h-1.5 w-1.5" : "h-2 w-2")}
          style={{ backgroundColor: tag.color }}
        />
      )}
      {tag.name}
      {onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          className="ml-0.5 hover:text-foreground"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </Badge>
  )
}

/**
 * Tag picker popover — lets users assign tags from existing list or create new ones.
 */
export function TagPicker({
  selectedTags,
  onTagsChange,
  siteId,
  board,
  noun = "Tags",
  quiet = false,
  open: controlledOpen,
  onOpenChange,
}: {
  selectedTags: Tag[]
  onTagsChange: (tagIds: number[], tags: Tag[]) => void
  siteId?: number | null
  /** A task board key: lists and creates that board's labels. */
  board?: string
  /** Trigger text; tasks call them labels. */
  noun?: string
  /** A borderless "+ Add" trigger for dense property lists. */
  quiet?: boolean
  /** Optional control, e.g. so a keyboard shortcut can open the picker. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
}) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const open = controlledOpen ?? uncontrolledOpen
  const setOpen = (next: boolean) => {
    setUncontrolledOpen(next)
    onOpenChange?.(next)
  }
  const [newTagName, setNewTagName] = useState("")
  const [newTagColor, setNewTagColor] = useState(TAG_COLORS[0].value)

  const { data: tagsData } = useQuery({
    queryKey: ["tags", { site_id: siteId, board }],
    queryFn: () => api.tags.list(tagListParams(siteId, board)),
  })

  const allTags: Tag[] = tagsData ?? []
  const selectedIds = new Set(selectedTags.map((t) => t.id))

  const createMutation = useMutation({
    mutationFn: (data: { name: string; color: string; site_id?: number | null; board?: string }) =>
      api.tags.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["tags"] })
      const newTag = (response as any)?.data ?? response
      // Auto-select the newly created tag
      const updatedTags = [...selectedTags, newTag]
      onTagsChange(updatedTags.map((t) => t.id), updatedTags)
      setNewTagName("")
      toast({ title: `Tag "${newTag.name}" created`, duration: 1500 })
    },
    onError: () => {
      toast({ title: "Failed to create tag", variant: "destructive" })
    },
  })

  const toggleTag = (tag: Tag) => {
    let updatedTags: Tag[]
    if (selectedIds.has(tag.id)) {
      updatedTags = selectedTags.filter((t) => t.id !== tag.id)
    } else {
      updatedTags = [...selectedTags, tag]
    }
    onTagsChange(updatedTags.map((t) => t.id), updatedTags)
  }

  const handleCreateTag = () => {
    const name = newTagName.trim()
    if (!name) return
    // A board label has no site; the API rejects the pair.
    createMutation.mutate(board ? { name, color: newTagColor, board } : { name, color: newTagColor, site_id: siteId ?? null })
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {quiet ? (
          <button
            type="button"
            aria-label={`Edit ${noun.toLowerCase()}`}
            className="inline-flex h-6 items-center gap-1 rounded-lg px-1.5 text-[13px] text-[#989898] hover:bg-black/[0.05] hover:text-[#191919] dark:text-[#6e7679] dark:hover:bg-[#b0c7d925] dark:hover:text-white"
          >
            <Plus className="h-3.5 w-3.5" />
            {selectedTags.length === 0 && `Add ${noun.toLowerCase().replace(/s$/, "")}`}
          </button>
        ) : (
        <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
          <Tags className="h-3.5 w-3.5" />
          {noun}
          {selectedTags.length > 0 && (
            <span className="ml-1 rounded-full bg-primary/10 text-primary px-1.5 text-[10px] font-medium">
              {selectedTags.length}
            </span>
          )}
        </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search tags..." />
          <CommandList>
            <CommandEmpty>
              <p className="text-sm text-muted-foreground px-2 py-1">No tags found</p>
            </CommandEmpty>
            <CommandGroup>
              {allTags.map((tag) => (
                <CommandItem
                  key={tag.id}
                  onSelect={() => toggleTag(tag)}
                  className="flex items-center gap-2"
                >
                  <div className={cn(
                    "h-4 w-4 rounded border flex items-center justify-center flex-shrink-0",
                    selectedIds.has(tag.id) ? "bg-primary border-primary" : "border-muted-foreground/30"
                  )}>
                    {selectedIds.has(tag.id) && <Check className="h-3 w-3 text-primary-foreground" />}
                  </div>
                  {tag.color && (
                    <span
                      className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: tag.color }}
                    />
                  )}
                  <span className="text-sm truncate">{tag.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="Create new tag">
              <div className="px-2 py-2 space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    placeholder="Tag name"
                    className="h-7 text-xs"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault()
                        handleCreateTag()
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    className="h-7 px-2"
                    onClick={handleCreateTag}
                    disabled={!newTagName.trim() || createMutation.isPending}
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {TAG_COLORS.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      className={cn(
                        "h-5 w-5 rounded-full border-2 transition-all",
                        newTagColor === color.value ? "border-foreground scale-110" : "border-transparent"
                      )}
                      style={{ backgroundColor: color.value }}
                      onClick={() => setNewTagColor(color.value)}
                      title={color.label}
                    />
                  ))}
                </div>
              </div>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

/**
 * Tag filter dropdown for filter controls row.
 * Lets user select one or more tags to filter by.
 */
export function TagFilterDropdown({
  selectedTagIds,
  onChange,
  siteId,
  board,
}: {
  selectedTagIds: number[]
  onChange: (tagIds: number[]) => void
  siteId?: number | null
  board?: string
}) {
  const [open, setOpen] = useState(false)

  const { data: tagsData } = useQuery({
    queryKey: ["tags", { site_id: siteId, board }],
    queryFn: () => api.tags.list(tagListParams(siteId, board)),
  })

  const allTags: Tag[] = tagsData ?? []
  const selectedSet = new Set(selectedTagIds)

  const toggleTag = (tagId: number) => {
    const next = new Set(selectedSet)
    if (next.has(tagId)) {
      next.delete(tagId)
    } else {
      next.add(tagId)
    }
    onChange(Array.from(next))
  }

  if (allTags.length === 0) return null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={selectedTagIds.length > 0 ? "secondary" : "outline"}
          size="sm"
          className="h-8 gap-1.5 text-xs"
        >
          <Tags className="h-3.5 w-3.5" />
          Tags
          {selectedTagIds.length > 0 && (
            <>
              <span className="ml-1 rounded-full bg-primary/10 text-primary px-1.5 text-[10px] font-medium">
                {selectedTagIds.length}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onChange([])
                }}
                className="ml-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-0" align="start">
        <Command>
          <CommandInput placeholder="Filter by tag..." />
          <CommandList>
            <CommandEmpty>No tags</CommandEmpty>
            <CommandGroup>
              {allTags.map((tag) => (
                <CommandItem
                  key={tag.id}
                  onSelect={() => toggleTag(tag.id)}
                  className="flex items-center gap-2"
                >
                  <div className={cn(
                    "h-4 w-4 rounded border flex items-center justify-center flex-shrink-0",
                    selectedSet.has(tag.id) ? "bg-primary border-primary" : "border-muted-foreground/30"
                  )}>
                    {selectedSet.has(tag.id) && <Check className="h-3 w-3 text-primary-foreground" />}
                  </div>
                  {tag.color && (
                    <span
                      className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: tag.color }}
                    />
                  )}
                  <span className="text-sm truncate">{tag.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

/**
 * Manage Tags dialog — lets users rename, recolor, and delete tags.
 * Can be triggered from the tag picker or settings.
 */
export function ManageTags({
  siteId,
  board,
  trigger,
  side,
  align = "start",
}: {
  siteId?: number | null
  board?: string
  /** Replaces the default "Manage Tags" button (e.g. a sidebar item). */
  trigger?: ReactElement
  side?: "top" | "right" | "bottom" | "left"
  align?: "start" | "center" | "end"
}) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [editingTag, setEditingTag] = useState<Tag | null>(null)
  const [editName, setEditName] = useState("")
  const [editColor, setEditColor] = useState("")

  const { data: tagsData } = useQuery({
    queryKey: ["tags", { site_id: siteId, board }],
    queryFn: () => api.tags.list(tagListParams(siteId, board)),
    enabled: open,
  })

  const allTags: Tag[] = tagsData ?? []

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { name?: string; color?: string } }) =>
      api.tags.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] })
      queryClient.invalidateQueries({ queryKey: ["tasks"] })
      queryClient.invalidateQueries({ queryKey: ["topics"] })
      setEditingTag(null)
      toast({ title: "Tag updated", duration: 1500 })
    },
    onError: () => {
      toast({ title: "Failed to update tag", variant: "destructive" })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.tags.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] })
      queryClient.invalidateQueries({ queryKey: ["tasks"] })
      queryClient.invalidateQueries({ queryKey: ["topics"] })
      toast({ title: "Tag deleted", duration: 1500 })
    },
    onError: () => {
      toast({ title: "Failed to delete tag", variant: "destructive" })
    },
  })

  const startEdit = (tag: Tag) => {
    setEditingTag(tag)
    setEditName(tag.name)
    setEditColor(tag.color || TAG_COLORS[0].value)
  }

  const saveEdit = () => {
    if (!editingTag || !editName.trim()) return
    updateMutation.mutate({
      id: editingTag.id,
      data: { name: editName.trim(), color: editColor },
    })
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger ?? (
          <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs text-muted-foreground">
            <Settings2 className="h-3 w-3" />
            Manage Tags
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" side={side} align={align}>
        <div className="space-y-3">
          <h4 className="text-sm font-medium">Manage Tags</h4>

          {allTags.length === 0 ? (
            <p className="text-xs text-muted-foreground">No tags yet. Create one from the tag picker.</p>
          ) : (
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {allTags.map((tag) => (
                <div key={tag.id}>
                  {editingTag?.id === tag.id ? (
                    <div className="space-y-2 p-2 rounded-md border bg-muted/50">
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="h-7 text-xs"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveEdit()
                          if (e.key === "Escape") setEditingTag(null)
                        }}
                        autoFocus
                      />
                      <div className="flex flex-wrap gap-1">
                        {TAG_COLORS.map((color) => (
                          <button
                            key={color.value}
                            type="button"
                            className={cn(
                              "h-4 w-4 rounded-full border-2 transition-all",
                              editColor === color.value ? "border-foreground scale-110" : "border-transparent"
                            )}
                            style={{ backgroundColor: color.value }}
                            onClick={() => setEditColor(color.value)}
                          />
                        ))}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" className="h-6 text-xs px-2" onClick={saveEdit} disabled={updateMutation.isPending}>
                          Save
                        </Button>
                        <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={() => setEditingTag(null)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted/50 group">
                      {tag.color && (
                        <span
                          className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: tag.color }}
                        />
                      )}
                      <span className="text-sm flex-1 truncate">{tag.name}</span>
                      <button
                        onClick={() => startEdit(tag)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5"
                        title="Edit tag"
                      >
                        <Pencil className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                      </button>
                      <button
                        onClick={() => deleteMutation.mutate(tag.id)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5"
                        title="Delete tag"
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
